/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import {
  Container, Stack, TextField, Button, MenuItem,
  Switch, FormControlLabel, Divider, Card, Box,
  Typography, Grid, Paper, List, ListItem,
  ListItemIcon, ListItemText, Checkbox, Chip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import SearchIcon from '@material-ui/icons/Search';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AESDecryptionParam } from '../../../../helpers/AesEncrypt';
import CommonPageHeader from '../../../CommonPageModules/CommonPageHeader';
import Page from '../../../Page';
import services from '../Services';
import permissionAuth from 'src/utils/permissionAuth';
import TokenDecoder from 'src/helpers/TokenDecoder';
import { trackPromise } from 'react-promise-tracker';
import { LoadingComponent } from 'src/pages/pageLoader';

const useStyles = makeStyles(() => ({
  root: { minHeight: '100%', paddingBottom: '24px', paddingTop: '24px' },
  cardPadding: { padding: '24px' }
}));

function pick(obj, keys, defaultValue = '') {
  if (!obj || typeof obj !== 'object') return defaultValue;
  for (const key of keys) {
    const found = Object.keys(obj).find(k => k.toLowerCase() === key.toLowerCase());
    const value = found !== undefined ? obj[found] : undefined;
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return defaultValue;
}

function pickInt(obj, keys, defaultValue = 0) {
  const v = parseInt(pick(obj, keys, defaultValue), 10);
  return Number.isNaN(v) ? defaultValue : v;
}

function pickBool(obj, keys, defaultValue = true) {
  const v = pick(obj, keys, defaultValue);
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const n = v.toLowerCase().trim();
    if (n === 'true' || n === '1') return true;
    if (n === 'false' || n === '0') return false;
  }
  if (typeof v === 'number') return v === 1;
  return defaultValue;
}

function pickDate(obj, keys) {
  const raw = pick(obj, keys, '');
  if (!raw) return '';
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return formatDate(raw);
  }
  const s = raw.toString().trim();
  if (s.includes('T')) return s.split('T')[0];
  const d = new Date(s);
  return !Number.isNaN(d.getTime()) ? formatDate(d) : '';
}

function pickIntArray(obj, keys) {
  const raw = pick(obj, keys, null);
  if (Array.isArray(raw)) return raw.map(id => parseInt(id, 10)).filter(id => id > 0);
  if (typeof raw === 'string' && raw.includes(',')) {
    const parsed = raw.split(',').map(id => parseInt(id.trim(), 10)).filter(id => id > 0);
    if (parsed.length > 0) return parsed;
  }
  const single = parseInt(raw, 10);
  return single > 0 ? [single] : [];
}

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toFactoryId(v) {
  return parseInt(v, 10) || 0;
}

function normalizeId(item) {
  return toFactoryId(item?.factoryID ?? item?.FactoryID ?? item);
}

export default function CustomerMasterAddEdit() {
  const classes = useStyles();
  const screenCode = 'CUSTOMERMASTER';
  const navigate = useNavigate();
  const { customerID } = useParams();
  const location = useLocation();

  const defaultGroupID = parseInt(TokenDecoder.getGroupFactoryToken()?.group, 10) || 0;

  const [groupList, setGroupList] = useState([]);
  const [customerTypeList, setCustomerTypeList] = useState([]);
  const [modeList, setModeList] = useState([]);
  const [provinceList, setProvinceList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [dsDivisionList, setDsDivisionList] = useState([]);
  const [gnDivisionList, setGnDivisionList] = useState([]);
  const [factoryList, setFactoryList] = useState([]);
  const [mappedOutlets, setMappedOutlets] = useState([]);
  const [checkedOutlets, setCheckedOutlets] = useState([]);
  const [isButtonEnable, setIsButtonEnable] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [lastSearchedCustomer, setLastSearchedCustomer] = useState(null);
  const [customerMasterID, setCustomerMasterID] = useState(0);
  const [inactiveTypeIDs, setInactiveTypeIDs] = useState([]);
  const [editingTypeID, setEditingTypeID] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingMappedRef = useRef([]);
  const hiddenMappedOutletIDsRef = useRef([]);
  const pendingUpdateRef = useRef(null);

  const [pageProps, setPageProps] = useState({
    editingID: 0,
    isEdit: false,
    submitButtonName: 'Save',
    pageName: 'Add Customer Details'
  });

  const [permissionList, setPermissions] = useState({ isGroupFilterEnabled: false });

  const getModeCode = (id) =>
    modeList.find(x => x.modeOfOperationID === parseInt(id, 10))?.modeOfOperationCode ?? '';

  const CustomerSchema = Yup.object().shape({
    groupID: Yup.number().min(1, 'Group Required').required('Required'),
    modeOfOperationID: Yup.number().min(1, 'Mode of Operation is Required').required('Required'),
    customerTypeIDs: Yup.array().min(1, 'At least one Customer Type is Required').required('Required'),

    customerName: Yup.string()
      .required('Customer Name is Required')
      .test('no-blank', 'Customer Name is Required', v => v && v.trim().length > 0),

    province: Yup.number().min(1, 'Province is Required').required('Required'),
    district: Yup.number().min(1, 'District is Required').required('Required'),
    dsDivision: Yup.number().min(1, 'DS Division is Required').required('Required'),
    gnDivision: Yup.number().min(1, 'GN Division is Required').required('Required'),

    addressLine1: Yup.string()
      .required('Address Line 1 is Required')
      .test('no-blank', 'Address Line 1 is Required', v => v && v.trim().length > 0),

    addressLine2: Yup.string()
      .required('Address Line 2 is Required')
      .test('no-blank', 'Address Line 2 is Required', v => v && v.trim().length > 0),

    city: Yup.number().min(1, 'City is Required').required('Required'),

    contactNumber: Yup.string()
      .required('Contact Number is Required')
      .test('no-blank', 'Contact Number is Required', v => v && v.trim().length > 0)
      .matches(/^[0-9]{10}$/, 'Must be 10 digits'),

    secondaryContact: Yup.string()
      .nullable()
      .matches(/^$|^[0-9]{10}$/, 'Must be 10 digits'),

    emailAddress: Yup.string().nullable().email('Invalid email address'),

    companyName: Yup.string()
      .nullable()
      .when('modeOfOperationID', {
        is: val => getModeCode(val) === '01',
        then: Yup.string()
          .required('Company Name is Required')
          .test('no-blank', 'Company Name is Required', v => !v || v.trim().length > 0)
      }),

    businessRegNo: Yup.string()
      .nullable()
      .when('modeOfOperationID', {
        is: val => getModeCode(val) === '01',
        then: Yup.string()
          .required('Please enter a valid value.')
          .test('no-blank', 'Please enter a valid value.', v => !v || v.trim().length > 0)
          .max(20, 'Maximum 20 characters allowed')
      }),

    dateOfRegistration: Yup.date()
      .nullable()
      .when('modeOfOperationID', {
        is: val => getModeCode(val) === '01',
        then: Yup.date().required('Date Of Registration is Required').max(new Date())
      }),

    nic: Yup.string()
      .nullable()
      .when('modeOfOperationID', {
        is: val => getModeCode(val) === '02',
        then: Yup.string()
          .required('NIC is Required')
          .test('no-blank', 'NIC is Required', v => !v || v.trim().length > 0)
          .matches(/^([0-9]{9}[vVxX]|[0-9]{12})$/, 'Invalid NIC')
      }),

    genderID: Yup.number()
      .nullable()
      .when('modeOfOperationID', {
        is: val => getModeCode(val) === '02',
        then: Yup.number().min(1, 'Gender is Required')
      })
  });

  const formik = useFormik({
    initialValues: {
      groupID: defaultGroupID,
      customerCode: '',
      modeOfOperationID: 0,
      customerTypeIDs: [],
      customerName: '',
      companyName: '',
      businessRegNo: '',
      dateOfRegistration: '',
      nic: '',
      genderID: 0,
      province: 0,
      district: 0,
      dsDivision: 0,
      gnDivision: 0,
      addressLine1: '',
      addressLine2: '',
      city: 0,
      contactNumber: '',
      secondaryContact: '',
      emailAddress: '',
      mappedOutlets: [],
      isActive: true
    },
    validationSchema: CustomerSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const trimFields = ['customerName', 'companyName', 'businessRegNo', 'addressLine1', 'addressLine2', 'contactNumber', 'nic'];
        const trimmedValues = { ...values };
        trimFields.forEach(k => { if (trimmedValues[k]) trimmedValues[k] = trimmedValues[k].trim(); });

        const fieldsToCheck = [
          { key: 'customerName', label: 'Customer Name' },
          { key: 'addressLine1', label: 'Address Line 1' },
          { key: 'addressLine2', label: 'Address Line 2' },
          { key: 'contactNumber', label: 'Contact Number' },
          ...(selectedModeCode === '01'
            ? [{ key: 'companyName', label: 'Company Name' }, { key: 'businessRegNo', label: 'Business Registration Number' }]
            : []),
          ...(selectedModeCode === '02' ? [{ key: 'nic', label: 'NIC Number' }] : [])
        ];

        for (const field of fieldsToCheck) {
          const original = values[field.key] || '';
          if (original !== original.trim()) {
            toast.error(`${field.label} should not have leading or trailing spaces`, { position: 'bottom-right' });
            setSubmitting(false);
            return;
          }
        }

        const shouldUpdate = pageProps.isEdit || parseInt(pageProps.editingID, 10) > 0;
        if (shouldUpdate) {
          // show confirmation dialog before proceeding
          if (!trimmedValues.isActive) {
            pendingUpdateRef.current = trimmedValues;
            setConfirmOpen(true);
            setSubmitting(false);
            return;
          }
          await trackPromise(UpdateCustomer(trimmedValues));
        } else {
          await trackPromise(SaveCustomer(trimmedValues));
        }
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, setValues, handleSubmit, getFieldProps, values, setFieldValue, isSubmitting, resetForm } = formik;

  const normalizeMappedOutlets = (source, factories = []) => {
    if (!Array.isArray(source)) return [];
    const hasFactories = factories.length > 0;
    return source
      .map(item => {
        const id = typeof item === 'object'
          ? toFactoryId(item.factoryID ?? item.FactoryID ?? item.id ?? item.ID)
          : toFactoryId(item);
        if (!id) return null;
        const matched = factories.find(f => toFactoryId(f.factoryID ?? f.FactoryID) === id);
        if (matched) return matched;
        if (hasFactories) return null;
        if (typeof item === 'object') return { ...item, factoryID: id, factoryName: item.factoryName ?? item.FactoryName ?? String(id) };
        return { factoryID: id, factoryName: String(id) };
      })
      .filter(Boolean)
      .filter((item, idx, arr) => arr.findIndex(v => normalizeId(v) === normalizeId(item)) === idx);
  };

  const availableOutlets = factoryList.filter(
    f => toFactoryId(f.factoryID ?? f.FactoryID) > 0 &&
      !mappedOutlets.some(m => normalizeId(m) === toFactoryId(f.factoryID ?? f.FactoryID))
  );

  const leftChecked = availableOutlets.filter(v =>
    checkedOutlets.some(i => normalizeId(i) === toFactoryId(v.factoryID ?? v.FactoryID))
  );
  const rightChecked = mappedOutlets.filter(v =>
    checkedOutlets.some(i => normalizeId(i) === normalizeId(v))
  );

  const outletNot = (source, targets) =>
    source.filter(v => !targets.some(t => normalizeId(t) === normalizeId(v)));


  async function GetAllGroups() {
    const res = await services.GetAllGroupsForDropDown();
    if (res) setGroupList(res);
  }

  async function GetModes() {
    const res = await services.getModeOfOperation();
    if (res) setModeList(res);
  }

  async function GetCustomerTypes() {
    const res = await services.getAvailableCustomerTypes();
    if (res) setCustomerTypeList(res);
  }

  async function GetOutletByGroupID(groupID, customerTypeIDs) {
    const res = await services.GetOutletByGroupID(groupID, customerTypeIDs);
    const outletList = Array.isArray(res) ? res : [];
    setFactoryList(outletList);
    setCheckedOutlets([]);
    return outletList;
  }

  async function GetProvinceForDropdown() {
    const res = await services.GetProvinceForDropdown();
    if (res) setProvinceList(res);
  }

  async function GetDistrictForDropdown(province) {
    if (parseInt(province, 10) > 0) {
      const res = await services.GetDistrictForDropdown(province);
      setDistrictList(res || []);
    } else {
      setDistrictList([]);
    }
  }

  async function GetCityForDropdown(province, district) {
    if (parseInt(province, 10) > 0 && parseInt(district, 10) > 0) {
      const res = await services.GetCityForDropdown(province, district);
      setCityList(res || []);
    } else {
      setCityList([]);
    }
  }

  async function GetDsDivisionsForDropdown(province, district, city) {
    if (parseInt(province, 10) > 0 && parseInt(district, 10) > 0 && parseInt(city, 10) > 0) {
      const res = await services.GetDsDivisionsForDropdown({
        provinceID: parseInt(province, 10),
        districtID: parseInt(district, 10),
        cityID: parseInt(city, 10)
      });
      setDsDivisionList(res || []);
    } else {
      setDsDivisionList([]);
    }
  }

  async function GetGnDivisionsForDropdown(province, district, city, dsDivision) {
    if ([province, district, city, dsDivision].every(v => parseInt(v, 10) > 0)) {
      const res = await services.GetGnDivisionsForDropdown({
        provinceID: parseInt(province, 10),
        districtID: parseInt(district, 10),
        cityID: parseInt(city, 10),
        dsDivisionID: parseInt(dsDivision, 10)
      });
      setGnDivisionList(res || []);
    } else {
      setGnDivisionList([]);
    }
  }

  async function GetCustomerDetails(id, customerName, keepGroupAndMode = true, clickedMappingID = null, clickedRowIsActive = null) {
    const res = await services.GetCustomerDetailsByID(id, customerName);
    if (!res) return;

    setSearchError('');

    const customerTypeDetails = Array.isArray(res.customerTypeDetails) ? res.customerTypeDetails
      : Array.isArray(res.CustomerTypeDetails) ? res.CustomerTypeDetails
        : [];
    const rowIsActive = clickedRowIsActive !== null
      ? clickedRowIsActive
      : customerTypeDetails.every(t => t.isActive === true || t.IsActive === true);
    const activeTypeIDs = customerTypeDetails
      .filter(t => t.isActive === true || t.IsActive === true)
      .map(t => parseInt(t.customerTypeID ?? t.CustomerTypeID ?? t.typeID ?? t.TypeID, 10))
      .filter(id => id > 0);

    let resolvedTypeIDs;
    if (!rowIsActive && clickedMappingID != null) {
      const clickedEntry = customerTypeDetails.find(
        t => (t.mappingID ?? t.MappingID) === clickedMappingID
      );
      const clickedTypeID = clickedEntry
        ? parseInt(clickedEntry.customerTypeID ?? clickedEntry.CustomerTypeID ?? clickedEntry.typeID ?? clickedEntry.TypeID, 10)
        : null;
      const combined = clickedTypeID && clickedTypeID > 0
        ? [clickedTypeID, ...activeTypeIDs.filter(id => id !== clickedTypeID)]
        : activeTypeIDs;
      resolvedTypeIDs = combined;
    } else {
      resolvedTypeIDs = activeTypeIDs;
    }

    const customerIsActive = rowIsActive;

    if (!rowIsActive && clickedMappingID != null) {
      const clickedEntry = customerTypeDetails.find(
        t => (t.mappingID ?? t.MappingID) === clickedMappingID
      );
      const clickedTypeID = clickedEntry
        ? parseInt(clickedEntry.customerTypeID ?? clickedEntry.CustomerTypeID ?? clickedEntry.typeID ?? clickedEntry.TypeID, 10)
        : null;
      setInactiveTypeIDs(clickedTypeID && clickedTypeID > 0 ? [clickedTypeID] : []);
      // Only scope outlet list for inactive records
      setEditingTypeID(clickedTypeID && clickedTypeID > 0 ? clickedTypeID : 0);
    } else {
      setInactiveTypeIDs([]);
      setEditingTypeID(0);
    }

    let scopedTypeID = 0;
    if (!rowIsActive && clickedMappingID != null) {
      const clickedEntry = customerTypeDetails.find(
        t => (t.mappingID ?? t.MappingID) === clickedMappingID
      );
      scopedTypeID = clickedEntry
        ? parseInt(clickedEntry.customerTypeID ?? clickedEntry.CustomerTypeID ?? clickedEntry.typeID ?? clickedEntry.TypeID, 10) || 0
        : 0;
    }

    const formValues = {
      groupID: keepGroupAndMode
        ? values.groupID
        : pickInt(res, ['groupID', 'GroupID', 'group', 'groupId'], values.groupID),

      customerCode: pick(res, ['customerCode', 'CustomerCode'], ''),

      modeOfOperationID: (() => {
        if (keepGroupAndMode) return values.modeOfOperationID;
        const fromID = pickInt(res, ['modeOfOperationID', 'ModeOfOperationID', 'modeOfOperationId', 'modeID', 'ModeID', 'modeId'], 0);
        if (fromID > 0) return fromID;
        const code = pick(res, ['modeOfOperationCode', 'ModeOfOperationCode', 'modeCode', 'ModeCode'], '').trim();
        const matched = modeList.find(m => (m.modeOfOperationCode || '').trim() === code);
        return matched ? parseInt(matched.modeOfOperationID, 10) : values.modeOfOperationID;
      })(),

      customerTypeIDs: (() => {
        if (resolvedTypeIDs.length > 0) return resolvedTypeIDs;
        const ids = pickIntArray(res, ['customerTypeIDs', 'CustomerTypeIDs', 'customerTypeID', 'CustomerTypeID', 'customerTypeId', 'typeID', 'TypeID']);
        return ids.length > 0 ? ids : (values.customerTypeIDs || []);
      })(),

      customerName: pick(res, ['customerName', 'CustomerName'], ''),
      companyName: pick(res, ['companyName', 'CompanyName'], ''),
      businessRegNo: pick(res, ['businessRegNo', 'BusinessRegNo', 'brn', 'BRN', 'businessRegistrationNo', 'BusinessRegistrationNo'], ''),
      dateOfRegistration: pickDate(res, ['dateOfRegistration', 'DateOfRegistration']),
      nic: pick(res, ['nic', 'NIC', 'nicBrn', 'NicBrn'], ''),
      genderID: pickInt(res, ['genderID', 'GenderID', 'genderId'], 0),
      province: pickInt(res, ['province', 'Province', 'provinceID', 'ProvinceID', 'provinceId'], 0),
      district: pickInt(res, ['district', 'District', 'districtID', 'DistrictID', 'districtId'], 0),
      city: pickInt(res, ['city', 'City', 'cityID', 'CityID', 'cityId'], 0),
      dsDivision: pickInt(res, ['dsDivision', 'DsDivision', 'DSDivision', 'dsDivisionID', 'DSDivisionID', 'dsDivisionId'], 0),
      gnDivision: pickInt(res, ['gnDivision', 'GnDivision', 'GNDivision', 'gnDivisionID', 'GNDivisionID', 'gnDivisionId'], 0),
      addressLine1: pick(res, ['addressLine1', 'AddressLine1'], ''),
      addressLine2: pick(res, ['addressLine2', 'AddressLine2'], ''),
      contactNumber: pick(res, ['contactNumber', 'ContactNumber', 'mobile', 'Mobile'], ''),
      secondaryContact: pick(res, ['secondaryContact', 'SecondaryContact'], ''),
      emailAddress: pick(res, ['emailAddress', 'EmailAddress'], ''),
      mappedOutlets: [],
      isActive: customerIsActive
    };

    const { province, district, city, dsDivision } = formValues;
    await Promise.all([
      GetDistrictForDropdown(province),
      GetCityForDropdown(province, district),
      GetDsDivisionsForDropdown(province, district, city),
      GetGnDivisionsForDropdown(province, district, city, dsDivision)
    ]);

    const resolvedModeCode = modeList.find(
      m => m.modeOfOperationID === formValues.modeOfOperationID
    )?.modeOfOperationCode ?? '';

    let outletList = [];
    if (resolvedModeCode === '01' && formValues.groupID > 0 && formValues.customerTypeIDs.length > 0) {
      // In edit fetch only that mapping's outlets
      const typeIDsForOutlets = clickedMappingID != null && scopedTypeID > 0
        ? [scopedTypeID]
        : formValues.customerTypeIDs;
      outletList = await GetOutletByGroupID(formValues.groupID, typeIDsForOutlets);
    } else {
      setFactoryList([]);
      setMappedOutlets([]);
      setCheckedOutlets([]);
    }

    const apiMappedIDs = (() => {
      const arr = res.mappedOutletIDList ?? res.MappedOutletIDList ?? res.mappedOutletIdList ?? null;
      if (Array.isArray(arr)) return arr.map(v => parseInt(v, 10)).filter(v => v > 0);
      return [];
    })();

    if (!rowIsActive) {
      hiddenMappedOutletIDsRef.current = apiMappedIDs;
      pendingMappedRef.current = [];
      formValues.mappedOutlets = [];
      setValues(formValues);
      setMappedOutlets([]);
      setCheckedOutlets([]);
    } else {
      hiddenMappedOutletIDsRef.current = [];

      const rawMapped = (() => {
        const arr = pick(res, ['mappedOutlets', 'MappedOutlets', 'outlets', 'Outlets'], null);
        if (Array.isArray(arr)) return arr;
        const str = pick(res, ['mappedOutletsString', 'MappedOutletsString', 'mappedOutletString', 'MappedOutletString'], '');
        if (str && str.trim()) {
          return str.split(',').map(v => parseInt(v.trim(), 10)).filter(v => !Number.isNaN(v) && v > 0);
        }
        return apiMappedIDs;
      })();

      const normalizedMapped = normalizeMappedOutlets(rawMapped, outletList);
      pendingMappedRef.current = normalizedMapped;
      formValues.mappedOutlets = normalizedMapped;
      setValues(formValues);
      setMappedOutlets(normalizedMapped);
      setCheckedOutlets([]);
    }

    setCustomerMasterID(pickInt(res, ['customerID', 'CustomerID', 'customerId', 'CustomerId'], 0));
  }

  async function getPermissions() {
    const permissions = await permissionAuth.getPermissionsByScreen(screenCode);
    if (!permissions.find(p => p.permissionCode === 'VIEWCUSTOMERMASTER')) navigate('/404');

    setPermissions({
      isGroupFilterEnabled: permissions.some(p => p.permissionCode === 'GROUPDROPDOWN')
    });

    if (!customerID || customerID === '0') {
      setFieldValue('groupID', parseInt(TokenDecoder.getGroupFactoryToken().group, 10));
    }
    trackPromise(GetAllGroups());
  }

  useEffect(() => {
    getPermissions();
    trackPromise(GetModes());
    trackPromise(GetCustomerTypes());
    trackPromise(GetProvinceForDropdown());

    if (customerID && customerID !== '0') {
      try {
        const decrypted = AESDecryptionParam(customerID);
        if (decrypted && decrypted !== '0') {
          const editData = JSON.parse(sessionStorage.getItem('customerEditData') || 'null');
          setPageProps({
            editingID: parseInt(decrypted, 10),
            isEdit: true,
            submitButtonName: 'Update',
            pageName: 'Edit Customer Details'
          });
          trackPromise(GetCustomerDetails(
            decrypted,
            editData?.customerName,
            false,
            editData?.mappingID ?? null,
            editData?.rowIsActive ?? null
          ));
          sessionStorage.removeItem('customerEditData');
        }
      } catch (e) {
        console.error('Decryption error', e);
      }
    }
  }, [customerID, location.key]);

  useEffect(() => {
    if (!pageProps.isEdit) trackPromise(GetDistrictForDropdown(values.province));
  }, [values.province]);

  useEffect(() => {
    if (!pageProps.isEdit) trackPromise(GetCityForDropdown(values.province, values.district));
  }, [values.province, values.district]);

  useEffect(() => {
    if (!pageProps.isEdit) trackPromise(GetDsDivisionsForDropdown(values.province, values.district, values.city));
  }, [values.province, values.district, values.city]);

  useEffect(() => {
    if (!pageProps.isEdit) trackPromise(GetGnDivisionsForDropdown(values.province, values.district, values.city, values.dsDivision));
  }, [values.province, values.district, values.city, values.dsDivision]);

  useEffect(() => {
    const groupID = parseInt(values.groupID, 10);
    const modeCode = getModeCode(values.modeOfOperationID);
    const typeIDs = Array.isArray(values.customerTypeIDs) ? values.customerTypeIDs : [];

    if (groupID > 0 && modeCode === '01' && typeIDs.length > 0) {
      const currentMapped = pendingMappedRef.current.length > 0
        ? pendingMappedRef.current
        : mappedOutlets;

      setCheckedOutlets([]);
      // Scope outlet list to the clicked inactive mapping's type only; active records use all typeIDs
      const typeIDsForOutlets = pageProps.isEdit && editingTypeID > 0 && inactiveTypeIDs.length > 0
        ? [editingTypeID]
        : typeIDs;
      GetOutletByGroupID(groupID, typeIDsForOutlets).then(freshList => {
        const toRestore = pendingMappedRef.current.length > 0
          ? pendingMappedRef.current
          : currentMapped;

        pendingMappedRef.current = [];

        const reconciled = normalizeMappedOutlets(toRestore, freshList);
        setMappedOutlets(reconciled);
        setFieldValue('mappedOutlets', reconciled);
      });
    } else {
      setFactoryList([]);
      setMappedOutlets([]);
      setCheckedOutlets([]);
      setFieldValue('mappedOutlets', []);
    }
  }, [values.groupID, values.modeOfOperationID, values.customerTypeIDs, modeList]);


  const getMappedOutletIdList = () => {
    const visible = mappedOutlets.map(item => normalizeId(item)).filter(id => id > 0);
    // Merge hidden IDs (from inactive record's mappedOutletIDList) with the visible ones (deduped)
    const hidden = hiddenMappedOutletIDsRef.current.filter(id => id > 0 && !visible.includes(id));
    return [...visible, ...hidden];
  };

  async function SaveCustomer(model) {
    const identifier = (selectedModeCode === '01' ? model.businessRegNo : model.nic || '').toString().trim().toLowerCase();

    if (identifier) {
      const existing = await services.SearchCustomerByNicOrBrn(model.groupID, model.modeOfOperationID, identifier);
      if (existing?.customerID > 0) {
        toast.error('The entered NIC/BRN already exists', { position: 'bottom-right' });
        return;
      }
    }

    const saveModel = {
      ...model,
      customerTypeIDs: (Array.isArray(model.customerTypeIDs) ? model.customerTypeIDs : []).map(id => ({ customerTypeID: parseInt(id, 10) })),
      province: parseInt(model.province, 10),
      district: parseInt(model.district, 10),
      city: parseInt(model.city, 10),
      dsDivision: parseInt(model.dsDivision, 10),
      gnDivision: parseInt(model.gnDivision, 10),
      modeOfOperationCode: selectedModeCode,
      dateOfRegistration: model.dateOfRegistration === '' ? null : model.dateOfRegistration,
      mappedOutlets: getMappedOutletIdList(),
      createdBy: parseInt(TokenDecoder.getUserIDFromToken(), 10)
    };

    const res = await services.SaveCustomerRegistration(saveModel);
    if (res.statusCode === 'Success') {
      toast.success(res.message || 'Customer Registered Successfully', { position: 'bottom-right' });
      setIsButtonEnable(true);
      setTimeout(() => navigate('/dashboard/customermaster'), 2000);
    } else {
      toast.error(res.message, { position: 'bottom-right' });
    }
  }

  async function UpdateCustomer(model) {
    const updateModel = {
      ...model,
      customerTypeIDs: (Array.isArray(model.customerTypeIDs) ? model.customerTypeIDs : []).map(id => ({ customerTypeID: parseInt(id, 10) })),
      customerMasterID: customerMasterID,
      customerID: pageProps.editingID,
      province: parseInt(model.province, 10),
      district: parseInt(model.district, 10),
      city: parseInt(model.city, 10),
      dsDivision: parseInt(model.dsDivision, 10),
      gnDivision: parseInt(model.gnDivision, 10),
      customerName: model.customerName?.trim(),
      companyName: model.companyName?.trim(),
      businessRegNo: model.businessRegNo?.trim(),
      modeOfOperationCode: selectedModeCode,
      dateOfRegistration: model.dateOfRegistration === '' ? null : model.dateOfRegistration,
      mappedOutlets: getMappedOutletIdList(),
      createdBy: parseInt(TokenDecoder.getUserIDFromToken(), 10)
    };

    const res = await services.UpdateCustomerRegistration(updateModel);
    if (res.statusCode === 'Success') {
      toast.success(res.message || 'Customer Updated Successfully', { position: 'bottom-right' });
      setIsButtonEnable(true);
      setTimeout(() => navigate('/dashboard/customermaster'), 2000);
    } else {
      toast.error(res.message, { position: 'bottom-right' });
    }
  }

  const handleSearchByNicOrBusinessRegNo = async () => {
    if (selectedModeCode !== '01' && selectedModeCode !== '02') return;
    if (parseInt(values.groupID, 10) <= 0) { setSearchError('Please select a group before searching'); return; }

    const searchValue = selectedModeCode === '01' ? values.businessRegNo : values.nic;
    const searchLabel = selectedModeCode === '01' ? 'Business Registration Number' : 'NIC Number';

    if (!searchValue?.toString().trim()) { setSearchError(`Please enter ${searchLabel}`); return; }

    const res = await services.SearchCustomerByNicOrBrn(values.groupID, values.modeOfOperationID, searchValue);
    if (res) {
      const detailed = await services.GetCustomerDetailsByID(res.customerID, res.customerName ?? res.CustomerName);
      const source = detailed || res;

      const matchedID = pickInt(source, ['customerID', 'CustomerID', 'customerId', 'CustomerId'],
        pickInt(res, ['customerID', 'CustomerID', 'customerId', 'CustomerId'], 0));

      const sourceTypeDetails = Array.isArray(source.customerTypeDetails) ? source.customerTypeDetails
        : Array.isArray(source.CustomerTypeDetails) ? source.CustomerTypeDetails
          : [];
      const sourceActiveTypeIDs = sourceTypeDetails
        .filter(t => t.isActive === true || t.IsActive === true)
        .map(t => parseInt(t.customerTypeID ?? t.CustomerTypeID ?? t.typeID ?? t.TypeID, 10))
        .filter(id => id > 0);
      const resolvedTypeIDs = sourceActiveTypeIDs.length > 0
        ? sourceActiveTypeIDs
        : pickIntArray(source, ['customerTypeIDs', 'CustomerTypeIDs', 'customerTypeID', 'CustomerTypeID', 'customerTypeId', 'typeID', 'TypeID']);

      setLastSearchedCustomer({
        customerID: matchedID,
        modeCode: selectedModeCode,
        identifier: searchValue.toString().trim().toLowerCase(),
        customerTypeIDs: resolvedTypeIDs,
        isActive: pickBool(source, ['isActive', 'IsActive'], true)
      });
      setSearchError('');

      await GetCustomerDetails(matchedID, res.customerName ?? res.CustomerName, true);
      setPageProps(prev => ({ ...prev, editingID: matchedID, submitButtonName: 'Update' }));
      toast.success('Customer found and details loaded', { position: 'bottom-right' });
      return;
    }

    setLastSearchedCustomer(null);
    setPageProps(prev => ({ ...prev, editingID: 0, submitButtonName: 'Save' }));
    setSearchError(`${searchLabel} not found`);
    toast.error(`${searchLabel} not found`, { position: 'bottom-right' });
  };

  const handleSearchFieldChange = field => e => {
    setSearchError('');
    setLastSearchedCustomer(null);
    if (!pageProps.isEdit) setPageProps(prev => ({ ...prev, editingID: 0, submitButtonName: 'Save' }));
    setMappedOutlets([]);
    setCheckedOutlets([]);
    setFieldValue(field, e.target.value);
  };

  const handleClearSearchError = field => e => {
    if (searchError) setSearchError('');
    setFieldValue(field, e.target.value);
  };

  const handleModeChange = e => {
    const mode = parseInt(e.target.value, 10);
    setSearchError('');
    setLastSearchedCustomer(null);
    if (!pageProps.isEdit) setPageProps(prev => ({ ...prev, editingID: 0, submitButtonName: 'Save' }));
    setMappedOutlets([]);
    setCheckedOutlets([]);
    setFieldValue('mappedOutlets', []);
    setFieldValue('modeOfOperationID', mode);
    ['customerName', 'companyName', 'businessRegNo', 'dateOfRegistration', 'nic'].forEach(k => setFieldValue(k, ''));
    setFieldValue('genderID', 0);
  };

  const handleProvinceChange = e => {
    const province = parseInt(e.target.value, 10) || 0;
    setFieldValue('province', province);
    setFieldValue('district', 0);
    setFieldValue('city', 0);
    setFieldValue('dsDivision', 0);
    setFieldValue('gnDivision', 0);
  };

  const handleDistrictChange = e => {
    setFieldValue('district', parseInt(e.target.value, 10) || 0);
    setFieldValue('city', 0);
    setFieldValue('dsDivision', 0);
    setFieldValue('gnDivision', 0);
  };

  const handleCityChange = e => {
    setFieldValue('city', parseInt(e.target.value, 10) || 0);
    setFieldValue('dsDivision', 0);
    setFieldValue('gnDivision', 0);
  };

  const handleDsDivisionChange = e => {
    setFieldValue('dsDivision', parseInt(e.target.value, 10) || 0);
    setFieldValue('gnDivision', 0);
  };

  const handleSearchFieldKeyDown = async e => {
    if (e.key === 'Enter') { e.preventDefault(); await trackPromise(handleSearchByNicOrBusinessRegNo()); }
  };

  const handleOutletToggle = value => () => {
    const idx = checkedOutlets.findIndex(i => normalizeId(i) === normalizeId(value));
    const next = [...checkedOutlets];
    if (idx === -1) next.push(value); else next.splice(idx, 1);
    setCheckedOutlets(next);
  };

  const handleOutletCheckedRight = () => {
    const next = mappedOutlets.concat(leftChecked);
    setMappedOutlets(next);
    setCheckedOutlets(outletNot(checkedOutlets, leftChecked));
    setFieldValue('mappedOutlets', next);
  };

  const handleOutletCheckedLeft = () => {
    const next = outletNot(mappedOutlets, rightChecked);
    setMappedOutlets(next);
    setCheckedOutlets(outletNot(checkedOutlets, rightChecked));
    setFieldValue('mappedOutlets', next);
  };

  const handleOutletSelectAll = items => e => {
    if (e.target.checked) {
      const toAdd = items.filter(item => !checkedOutlets.some(s => normalizeId(s) === normalizeId(item)));
      setCheckedOutlets([...checkedOutlets, ...toAdd]);
    } else {
      setCheckedOutlets(checkedOutlets.filter(item => !items.some(s => normalizeId(s) === normalizeId(item))));
    }
  };

  const allChecked = (checkedItems, items) =>
    items.length > 0 && items.every(item => checkedItems.some(s => normalizeId(s) === normalizeId(item)));

  const customOutletList = items => (
    <Paper style={{ height: 240, overflow: 'auto' }}>
      <ListItem role="listitem">
        <ListItemIcon>
          <Checkbox
            disabled={items.length === 0}
            color="secondary"
            onClick={handleOutletSelectAll(items)}
            checked={allChecked(checkedOutlets, items)}
          />
        </ListItemIcon>
        <ListItemText primary="Select All" />
      </ListItem>
      <List dense component="div" role="list">
        {items.map(value => (
          <ListItem key={value.factoryID} role="listitem" button onClick={handleOutletToggle(value)}>
            <ListItemIcon>
              <Checkbox
                checked={checkedOutlets.some(i => normalizeId(i) === normalizeId(value))}
                tabIndex={-1}
                color="secondary"
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={value.factoryName} />
          </ListItem>
        ))}
      </List>
    </Paper>
  );

  const handleClickClear = () => {
    const currentGroup = values.groupID;
    setLastSearchedCustomer(null);
    setSearchError('');
    setPageProps(prev => ({ ...prev, editingID: 0, submitButtonName: 'Save' }));
    resetForm();
    setMappedOutlets([]);
    setCheckedOutlets([]);
    setFieldValue('mappedOutlets', []);
    setFieldValue('groupID', currentGroup);
  };

  const selectedModeCode = getModeCode(values.modeOfOperationID);
  const hasOutletData = factoryList.length > 0 || mappedOutlets.length > 0;
  const showSearchControls = !pageProps.isEdit && parseInt(pageProps.editingID, 10) === 0;

  const todayDate = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  return (
    <Page title="Customer Master | FAPS">
      <Container>
        <CommonPageHeader path="/dashboard/customermaster" btname="Back" pageName={pageProps.pageName} />
        <LoadingComponent />
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Box sx={{ mt: 3 }}>
              <Card className={classes.cardPadding}>
                <Stack spacing={3}>

                  {/* ── General Information ── */}
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>General Information</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        select fullWidth size="small" label="Group *"
                        {...getFieldProps('groupID')}
                        error={Boolean(touched.groupID && errors.groupID)}
                        helperText={touched.groupID && errors.groupID}
                        InputProps={{ readOnly: pageProps.isEdit || !permissionList.isGroupFilterEnabled }}
                      >
                        <MenuItem value={0}>Select Group</MenuItem>
                        {groupList.map(x => <MenuItem key={x.groupID} value={x.groupID}>{x.groupName}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        select fullWidth size="small" label="Mode of Operation *"
                        value={values.modeOfOperationID}
                        onChange={handleModeChange}
                        error={Boolean(touched.modeOfOperationID && errors.modeOfOperationID)}
                        helperText={touched.modeOfOperationID && errors.modeOfOperationID}
                        InputProps={{ readOnly: pageProps.isEdit, style: pageProps.isEdit ? { backgroundColor: '#f5f5f5' } : {} }}
                      >
                        <MenuItem value={0}>Select Mode</MenuItem>
                        {modeList.map(x => <MenuItem key={x.modeOfOperationID} value={x.modeOfOperationID}>{x.modeOfOperationName}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        select fullWidth size="small" label="Customer Type *"
                        value={values.customerTypeIDs}
                        onChange={e => {
                          const val = e.target.value;
                          if (val.includes('__all__')) {
                            setFieldValue('customerTypeIDs',
                              values.customerTypeIDs.length === customerTypeList.length
                                ? [] : customerTypeList.map(x => x.customerTypeID));
                          } else {
                            setFieldValue('customerTypeIDs', val);
                          }
                        }}
                        onBlur={() => formik.setFieldTouched('customerTypeIDs', true)}
                        error={Boolean(touched.customerTypeIDs && errors.customerTypeIDs)}
                        helperText={touched.customerTypeIDs && errors.customerTypeIDs}
                        inputProps={{ readOnly: inactiveTypeIDs.length > 0 }}
                        style={inactiveTypeIDs.length > 0 ? { backgroundColor: '#f5f5f5' } : {}}
                        SelectProps={{
                          multiple: true,
                          renderValue: selected => {
                            const visible = selected.filter(
                              id => inactiveTypeIDs.length === 0 || inactiveTypeIDs.includes(id)
                            );
                            const first = visible[0];
                            const rest = visible.length - 1;
                            const firstFound = customerTypeList.find(x => x.customerTypeID === first);
                            return (
                              <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                {firstFound && <Chip label={firstFound.customerTypeName} size="small" />}
                                {rest > 0 && (
                                  <Chip label={`+${rest} more`} size="small" style={{ backgroundColor: '#e0e0e0' }} />
                                )}
                              </Box>
                            );
                          }
                        }}
                      >
                        <MenuItem value="__all__">
                          <Checkbox
                            checked={customerTypeList.length > 0 && values.customerTypeIDs.length === customerTypeList.length}
                            indeterminate={values.customerTypeIDs.length > 0 && values.customerTypeIDs.length < customerTypeList.length}
                            color="secondary"
                          />
                          Select All
                        </MenuItem>
                        {customerTypeList.map(x => (
                          <MenuItem key={x.customerTypeID} value={x.customerTypeID}>
                            <Checkbox checked={values.customerTypeIDs.indexOf(x.customerTypeID) > -1} color="secondary" />
                            {x.customerTypeName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  <Divider />

                  {/* ── Mode 01 – Business fields ── */}
                  {selectedModeCode === '01' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <TextField
                            fullWidth size="small" label="Business Registration Number *"
                            name="businessRegNo" value={values.businessRegNo}
                            onChange={handleSearchFieldChange('businessRegNo')}
                            onBlur={formik.handleBlur}
                            onKeyDown={showSearchControls ? handleSearchFieldKeyDown : undefined}
                            error={Boolean((touched.businessRegNo && errors.businessRegNo) || searchError)}
                            helperText={(touched.businessRegNo && errors.businessRegNo) || searchError}
                          />
                          {showSearchControls && (
                            <Button variant="outlined" color="secondary" style={{ minWidth: 64, height: 39 }}
                              onClick={() => trackPromise(handleSearchByNicOrBusinessRegNo())}
                              startIcon={<SearchIcon />} />
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth size="small" label="Date of Registration *" type="date"
                          InputLabelProps={{ shrink: true }} inputProps={{ max: todayDate }}
                          {...getFieldProps('dateOfRegistration')}
                          onChange={handleClearSearchError('dateOfRegistration')}
                          error={Boolean(touched.dateOfRegistration && errors.dateOfRegistration)}
                          helperText={touched.dateOfRegistration && errors.dateOfRegistration}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Customer Name *"
                          {...getFieldProps('customerName')}
                          onChange={handleClearSearchError('customerName')}
                          error={Boolean(touched.customerName && errors.customerName)}
                          helperText={touched.customerName && errors.customerName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Company Name *"
                          {...getFieldProps('companyName')}
                          onChange={handleClearSearchError('companyName')}
                          error={Boolean(touched.companyName && errors.companyName)}
                          helperText={touched.companyName && errors.companyName}
                        />
                      </Grid>
                      {pageProps.isEdit && (
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" label="Customer Code"
                            {...getFieldProps('customerCode')}
                            InputProps={{ readOnly: true }}
                            style={{ backgroundColor: '#f5f5f5' }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}

                  {/* ── Mode 02 – Individual (NIC) fields ── */}
                  {selectedModeCode === '02' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <TextField
                            fullWidth size="small" label="NIC Number *"
                            name="nic" value={values.nic}
                            onChange={handleSearchFieldChange('nic')}
                            onBlur={formik.handleBlur}
                            onKeyDown={showSearchControls ? handleSearchFieldKeyDown : undefined}
                            error={Boolean((touched.nic && errors.nic) || searchError)}
                            helperText={(touched.nic && errors.nic) || searchError}
                          />
                          {showSearchControls && (
                            <Button variant="outlined" color="secondary" style={{ minWidth: 64, height: 39 }}
                              onClick={() => trackPromise(handleSearchByNicOrBusinessRegNo())}
                              startIcon={<SearchIcon />} />
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Customer Name *"
                          {...getFieldProps('customerName')}
                          onChange={handleClearSearchError('customerName')}
                          error={Boolean(touched.customerName && errors.customerName)}
                          helperText={touched.customerName && errors.customerName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField select fullWidth size="small" label="Gender *"
                          {...getFieldProps('genderID')}
                          onChange={handleClearSearchError('genderID')}
                          error={Boolean(touched.genderID && errors.genderID)}
                          helperText={touched.genderID && errors.genderID}
                        >
                          <MenuItem value={0}>Select Gender</MenuItem>
                          <MenuItem value={1}>Male</MenuItem>
                          <MenuItem value={2}>Female</MenuItem>
                          <MenuItem value={3}>Other</MenuItem>
                          <MenuItem value={4}>Prefer not to mention</MenuItem>
                        </TextField>
                      </Grid>
                      {pageProps.isEdit && (
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" label="Customer Code"
                            {...getFieldProps('customerCode')}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}

                  {/* ── Mode 03 – Other ── */}
                  {selectedModeCode === '03' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Customer Name *"
                          {...getFieldProps('customerName')}
                          error={Boolean(touched.customerName && errors.customerName)}
                          helperText={touched.customerName && errors.customerName}
                        />
                      </Grid>
                      {pageProps.isEdit && (
                        <Grid item xs={12} sm={6}>
                          <TextField fullWidth size="small" label="Customer Code"
                            {...getFieldProps('customerCode')}
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}

                  <Divider />

                  {/* ── Address ── */}
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>Address</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth size="small" label="Province *"
                        value={values.province} onChange={handleProvinceChange}
                        error={Boolean(touched.province && errors.province && !(parseInt(values.province, 10) > 0))}
                        helperText={touched.province && errors.province && !(parseInt(values.province, 10) > 0) ? errors.province : ''}
                      >
                        <MenuItem value={0}>Select Province</MenuItem>
                        {provinceList.map(x => <MenuItem key={x.provinceID} value={x.provinceID}>{x.provinceName}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth size="small" label="District *"
                        value={values.district} onChange={handleDistrictChange}
                        error={Boolean(touched.district && errors.district && !(parseInt(values.district, 10) > 0))}
                        helperText={touched.district && errors.district && !(parseInt(values.district, 10) > 0) ? errors.district : ''}
                      >
                        <MenuItem value={0}>Select District</MenuItem>
                        {districtList.map(x => <MenuItem key={x.districtID} value={x.districtID}>{x.districtName}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth size="small" label="City *"
                        value={values.city} onChange={handleCityChange}
                        error={Boolean(touched.city && errors.city && !(parseInt(values.city, 10) > 0))}
                        helperText={touched.city && errors.city && !(parseInt(values.city, 10) > 0) ? errors.city : ''}
                      >
                        <MenuItem value={0}>Select City</MenuItem>
                        {cityList.map(x => <MenuItem key={x.cityID} value={x.cityID}>{x.cityName}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth size="small" label="DS Division *"
                        value={values.dsDivision} onChange={handleDsDivisionChange}
                        error={Boolean(touched.dsDivision && errors.dsDivision && !(parseInt(values.dsDivision, 10) > 0))}
                        helperText={touched.dsDivision && errors.dsDivision && !(parseInt(values.dsDivision, 10) > 0) ? errors.dsDivision : ''}
                      >
                        <MenuItem value={0}>Select DS Division</MenuItem>
                        {dsDivisionList.map(x => <MenuItem key={x.dsDivisionID} value={x.dsDivisionID}>{x.dsDivisionName}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField select fullWidth size="small" label="GN Division *"
                        value={values.gnDivision}
                        onChange={e => setFieldValue('gnDivision', parseInt(e.target.value, 10) || 0)}
                        error={Boolean(touched.gnDivision && errors.gnDivision && !(parseInt(values.gnDivision, 10) > 0))}
                        helperText={touched.gnDivision && errors.gnDivision && !(parseInt(values.gnDivision, 10) > 0) ? errors.gnDivision : ''}
                      >
                        <MenuItem value={0}>Select GN Division</MenuItem>
                        {gnDivisionList.map(x => <MenuItem key={x.gnDivisionID} value={x.gnDivisionID}>{x.gnDivisionName}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Address Line 1 *"
                        {...getFieldProps('addressLine1')}
                        error={Boolean(touched.addressLine1 && errors.addressLine1 && !values.addressLine1?.trim())}
                        helperText={touched.addressLine1 && errors.addressLine1 && !values.addressLine1?.trim() ? errors.addressLine1 : ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Address Line 2 *"
                        {...getFieldProps('addressLine2')}
                        error={Boolean(touched.addressLine2 && errors.addressLine2 && !values.addressLine2?.trim())}
                        helperText={touched.addressLine2 && errors.addressLine2 && !values.addressLine2?.trim() ? errors.addressLine2 : ''}
                      />
                    </Grid>
                  </Grid>

                  <Divider />

                  {/* ── Contact Numbers ── */}
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>Contact Numbers</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Primary Contact *"
                        {...getFieldProps('contactNumber')}
                        error={Boolean(touched.contactNumber && errors.contactNumber)}
                        helperText={touched.contactNumber && errors.contactNumber}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Secondary Contact"
                        {...getFieldProps('secondaryContact')}
                        error={Boolean(touched.secondaryContact && errors.secondaryContact)}
                        helperText={touched.secondaryContact && errors.secondaryContact}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth size="small" label="Email Address"
                        {...getFieldProps('emailAddress')}
                        error={Boolean(touched.emailAddress && errors.emailAddress)}
                        helperText={touched.emailAddress && errors.emailAddress}
                      />
                    </Grid>
                  </Grid>

                  {/* ── Outlets (Mode 01 only) ── */}
                  {selectedModeCode === '01' && Array.isArray(values.customerTypeIDs) && values.customerTypeIDs.length > 0 && hasOutletData && (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Outlets</Typography>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={5}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Outlets</Typography>
                            {customOutletList(availableOutlets)}
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} alignItems="center" justifyContent="center">
                              <Button variant="outlined" size="small" onClick={handleOutletCheckedRight} disabled={!leftChecked.length}>&gt;</Button>
                              <Button variant="outlined" size="small" onClick={handleOutletCheckedLeft} disabled={!rightChecked.length}>&lt;</Button>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={5}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Mapped Outlets</Typography>
                            {customOutletList(mappedOutlets)}
                          </Grid>
                        </Grid>
                      </Box>
                    </>
                  )}

                  {/* ── Footer ── */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <FormControlLabel
                      control={<Switch {...getFieldProps('isActive')} checked={values.isActive} color="error" />}
                      label="Is Active"
                    />
                    <Box>
                      <Button color="error" variant="outlined" sx={{ mr: 1 }}
                        onClick={handleClickClear}
                        disabled={pageProps.isEdit || Boolean(lastSearchedCustomer)}
                      >
                        Clear
                      </Button>
                      <Button type="submit" variant="contained" color="secondary"
                        disabled={isSubmitting || isButtonEnable}
                      >
                        {pageProps.submitButtonName}
                      </Button>
                    </Box>
                  </Stack>

                </Stack>
              </Card>
            </Box>
          </Form>
        </FormikProvider>
        <ToastContainer />

        {/* ── Inactive record update confirmation dialog ── */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirm Update</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to inactivate customer profile?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} color="error" variant="outlined">
              Cancel
            </Button>
            <Button
              color="secondary"
              variant="contained"
              onClick={async () => {
                setConfirmOpen(false);
                if (pendingUpdateRef.current) {
                  await trackPromise(UpdateCustomer(pendingUpdateRef.current));
                  pendingUpdateRef.current = null;
                }
              }}
            >
              Yes, Proceed
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}